import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useGraphColors } from "@core";

import { BaseTimeSeriesChart, TimeSeriesData, YAxisConfig } from "../charts/BaseTimeSeriesChart";
import type { ChartSeriesData, DateRange, GraphSettings, Resolution } from "../shared/types";

// Series types for the energy consumption chart
enum EnergyComponentType {
  HEAT_ENERGY = "heat_energy",
  SUPPLY_TEMP = "supplytemp",
  RETURN_TEMP = "returntemp",
  RADIATOR_SUPPLY_TEMP = "radiator_system_supply_temp",
  OUTDOOR_TEMP = "outdoor_temp",
}

type EnergyConsumptionChartContainerProps = {
  data: ChartSeriesData[];
  isLoading?: boolean;
  chartHeight?: number;
  dateRange?: DateRange;
  resolution?: Resolution;
  graphSettings?: GraphSettings;
};

/**
 * EnergyConsumptionChartContainer - Container for energy consumption time series chart
 *
 * This component handles the specific data formatting and configuration for the energy consumption chart
 */
export const EnergyConsumptionChartContainer: React.FC<EnergyConsumptionChartContainerProps> = ({
  data,
  isLoading = false,
  chartHeight,
  dateRange,
  resolution,
  graphSettings,
}) => {
  const { t } = useTranslation(["propai", "extendView", "_common"]);
  const graphColors = useGraphColors();

  const SERIES_CONFIG = useMemo((): Record<
    EnergyComponentType,
    Omit<TimeSeriesData, "uid" | "data">
  > => {
    return {
      [EnergyComponentType.HEAT_ENERGY]: {
        name: t("text_heat_energy"),
        unit: " kWh",
        color: graphColors[0],
        yAxisIndex: 0,
        visible: true,
      },
      [EnergyComponentType.OUTDOOR_TEMP]: {
        name: t("text_outdoor_temperature"),
        unit: "°C",
        color: graphColors[1],
        yAxisIndex: 1,
        visible: true,
      },
      [EnergyComponentType.RADIATOR_SUPPLY_TEMP]: {
        name: t("text_radiator_supply_temperature"),
        unit: "°C",
        color: graphColors[2],
        yAxisIndex: 1,
        visible: false,
      },
      [EnergyComponentType.RETURN_TEMP]: {
        name: `${t("text_district_heat")} ${t("text_return_temperature")}`,
        unit: "°C",
        color: graphColors[3],
        yAxisIndex: 1,
        visible: false,
      },
      [EnergyComponentType.SUPPLY_TEMP]: {
        name: `${t("text_district_heat")} ${t("text_supply_temperature")}`,
        unit: "°C",
        color: graphColors[4],
        yAxisIndex: 1,
        visible: false,
      },
    };
  }, [t, graphColors]);

  const Y_AXIS_CONFIGS = useMemo((): YAxisConfig[] => {
    return [
      {
        title: { text: `${t("text_heat_energy")} (kWh)` },
        labels: {
          formatter: function () {
            const val = (this as any).value as number;
            return val.toFixed(2);
          },
        },
      },
      {
        title: { text: `${t("text_temperature")} (°C)` },
        labels: {
          formatter: function () {
            const val = (this as any).value as number;
            return val.toFixed(1);
          },
        },
      },
    ];
  }, [t]);
  // Transform meter data to time series format - stabilized to prevent flickering
  const series = useMemo((): TimeSeriesData[] => {
    if (!data || !Array.isArray(data)) return [];

    // Stabilize the series by ensuring consistent ordering and data structure
    return Object.entries(EnergyComponentType)
      .sort(([a], [b]) => a.localeCompare(b)) // Consistent ordering
      .map(([_, componentType]) => {
        const componentData = data.find((d) => d?.component === componentType);
        const config = SERIES_CONFIG[componentType];

        return {
          uid: componentType,
          name: config.name,
          data: componentData?.data ?? [],
          unit: config.unit,
          color: config.color,
          yAxisIndex: config.yAxisIndex,
          visible: config.visible,
        };
      });
  }, [data, SERIES_CONFIG]);

  return (
    <BaseTimeSeriesChart
      series={series}
      yAxisConfigs={Y_AXIS_CONFIGS}
      chartHeight={chartHeight}
      dateRange={dateRange}
      resolution={resolution}
      graphSettings={graphSettings}
      isLoading={isLoading}
      allowSeriesToggle={true}
    />
  );
};
